import React, { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Check, X as XIcon, Tag, ArrowRight, MagnifyingGlass, CaretLeft, CaretRight, EnvelopeSimple, ShieldCheck, Globe, Phone, SpinnerGap } from '@phosphor-icons/react';

const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  needs_review: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

export default function ReviewQueuePage() {
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null); // full company detail with contacts
  const [verifyingEmails, setVerifyingEmails] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, sort_by: 'created_at', sort_order: 'desc' };
      if (search) params.search = search;
      const { data } = await api.get('/companies', { params });
      // Filter to reviewable statuses and sort by score (highest first)
      const reviewable = data.items
        .filter(c => ['new', 'needs_review', 'processing'].includes(c.status))
        .sort((a, b) => (b.score || 0) - (a.score || 0));
      setCompanies(reviewable);
      setTotal(reviewable.length);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const current = companies[selectedIdx];

  // Fetch full company detail when selection changes
  useEffect(() => {
    if (!current?.id) { setDetail(null); return; }
    api.get(`/companies/${current.id}`).then(r => setDetail(r.data)).catch(() => setDetail(null));
  }, [current?.id]);

  const updateStatus = async (id, status) => {
    try {
      await api.post('/companies/bulk-action', { company_ids: [id], action: status === 'approved' ? 'approve' : 'reject' });
      const hint = status === 'approved'
        ? 'Approved! This lead is now eligible for export and Pro-Email push.'
        : 'Rejected. This lead will be excluded from campaign exports.';
      toast.success(hint);
      fetchQueue();
      if (selectedIdx >= companies.length - 1) setSelectedIdx(Math.max(0, selectedIdx - 1));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const bulkApproveAll = async () => {
    const ids = companies.map(c => c.id);
    if (ids.length === 0) return;
    try {
      await api.post('/companies/bulk-action', { company_ids: ids, action: 'approve' });
      toast.success(`Approved ${ids.length} leads`);
      fetchQueue();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const verifyEmails = async () => {
    const companyId = current?.id;
    if (!companyId || verifyingEmails) return;

    setVerifyingEmails(true);
    try {
      const { data } = await api.post(`/discovery/verify-mx/${companyId}`);
      const refreshed = await api.get(`/companies/${companyId}`);
      setDetail(refreshed.data);
      setCompanies(previous => previous.map(company => (
        company.id === companyId
          ? { ...company, score: refreshed.data.score, has_valid_email: refreshed.data.has_valid_email }
          : company
      )));
      toast.success(`Email verification complete: ${data.mx_confirmed} confirmed, ${data.invalid} invalid, ${data.risky} risky`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setVerifyingEmails(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading review queue...</div>;

  return (
    <div data-testid="review-queue-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">Review Queue</h1>
          <p className="text-sm text-gray-500 mt-1">{total} leads awaiting review. Sorted by highest score first. Approve to make campaign-ready, reject to exclude.</p>
        </div>
        {companies.length > 0 && (
          <Button onClick={bulkApproveAll} className="bg-black text-white hover:bg-gray-800" data-testid="bulk-approve-all-btn">
            <Check size={16} className="mr-2" /> Approve All ({total})
          </Button>
        )}
      </div>

      {companies.length === 0 ? (
        <div className="border border-gray-200 rounded-md bg-white p-12 text-center">
          <p className="text-gray-500 text-sm mb-2">No leads in review queue.</p>
          <p className="text-xs text-gray-400">Import companies or run a discovery job. Companies scoring above the campaign-ready threshold with valid emails are automatically promoted here.</p>
        </div>
      ) : (
        <div className="flex gap-6">
          {/* List panel */}
          <div className="w-1/3 border border-gray-200 rounded-md bg-white overflow-hidden">
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input placeholder="Filter..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 text-sm h-8 bg-white border-gray-200" />
              </div>
            </div>
            <div className="max-h-[calc(100vh-320px)] overflow-y-auto">
              {companies.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedIdx(i)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${i === selectedIdx ? 'bg-gray-50 border-l-2 border-l-gray-900' : ''}`}
                  data-testid={`queue-item-${c.id}`}
                >
                  <p className="text-sm font-medium text-gray-900 truncate">{c.company_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-xs text-gray-500">{c.normalized_domain || 'No domain'}</span>
                    <span className={`font-mono text-xs font-bold ${(c.score || 0) >= 60 ? 'text-emerald-700' : (c.score || 0) >= 40 ? 'text-amber-700' : 'text-gray-900'}`}>{c.score || 0}</span>
                    {c.has_valid_email && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Has verified email" />}
                    {c.contact_count > 0 && <span className="text-[10px] text-gray-400">{c.contact_count} contacts</span>}
                  </div>
                  <div className="flex gap-1 mt-1">
                    {(c.tags || []).slice(0, 3).map(t => (
                      <span key={t} className="text-[10px] font-mono bg-gray-100 text-gray-500 px-1 rounded">{t}</span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail panel */}
          {current && (
            <div className="flex-1 border border-gray-200 rounded-md bg-white overflow-y-auto max-h-[calc(100vh-220px)]" data-testid="review-detail-panel">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="font-heading text-xl font-bold text-gray-900">{current.company_name}</h2>
                    <p className="text-sm text-gray-500 mt-1">{current.industry_raw || 'No industry'} | {[current.city, current.region, current.country].filter(Boolean).join(', ')}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <button onClick={() => setSelectedIdx(i => Math.max(0, i - 1))} disabled={selectedIdx === 0} className="text-gray-400 hover:text-gray-900 disabled:opacity-30"><CaretLeft size={20} /></button>
                    <span className="text-xs font-mono text-gray-400">{selectedIdx + 1}/{companies.length}</span>
                    <button onClick={() => setSelectedIdx(i => Math.min(companies.length - 1, i + 1))} disabled={selectedIdx === companies.length - 1} className="text-gray-400 hover:text-gray-900 disabled:opacity-30"><CaretRight size={20} /></button>
                  </div>
                </div>

                {/* Key info row */}
                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                  {current.normalized_domain && <span className="flex items-center gap-1"><Globe size={14} /> <span className="font-mono">{current.normalized_domain}</span></span>}
                  {current.phone && <span className="flex items-center gap-1"><Phone size={14} /> {current.phone}</span>}
                  <span className="flex items-center gap-1"><EnvelopeSimple size={14} /> {current.contact_count || 0} contacts</span>
                </div>

                {/* Tags */}
                {current.tags && current.tags.length > 0 && (
                  <div className="flex gap-1.5 mb-4">
                    {current.tags.map(t => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono bg-gray-100 text-gray-700 border border-gray-200"><Tag size={10} />{t}</span>
                    ))}
                  </div>
                )}

                {/* Score breakdown */}
                <div className="mb-4 p-4 bg-gray-50 rounded-md">
                  <div className="flex items-center gap-4 mb-2">
                    <span className="text-xs font-mono uppercase text-gray-400">Score</span>
                    <span className="text-2xl font-mono font-bold text-gray-900">{current.score || 0}</span>
                    <span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${
                      (current.score || 0) >= 60 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      (current.score || 0) >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {(current.score || 0) >= 60 ? 'High' : (current.score || 0) >= 40 ? 'Medium' : 'Low'}
                    </span>
                  </div>
                  {detail?.score_details && (
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {[
                        { l: 'Industry', v: detail.score_details.industry_score },
                        { l: 'Website', v: detail.score_details.website_quality_score },
                        { l: 'Location', v: detail.score_details.location_score },
                        { l: 'Contacts', v: detail.score_details.contact_quality_score },
                        { l: 'Email', v: detail.score_details.email_quality_score },
                        { l: 'Custom', v: detail.score_details.custom_rule_score },
                      ].map(s => (
                        <div key={s.l} className="bg-white rounded px-2 py-1 border border-gray-100">
                          <p className="text-[10px] font-mono uppercase text-gray-400">{s.l}</p>
                          <p className="text-sm font-mono font-bold text-gray-800">{s.v || 0}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Contacts & Emails - the key decision data */}
                <div className="mb-5">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500">Contacts & Emails</h3>
                    {detail?.contacts && detail.contacts.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={verifyEmails}
                        disabled={verifyingEmails}
                        className="h-8 text-xs"
                        data-testid="review-verify-emails-btn"
                        title="Verify mail servers for every contact email, including generic inboxes such as info@ and sales@"
                      >
                        {verifyingEmails
                          ? <SpinnerGap size={14} className="mr-1.5 animate-spin" />
                          : <ShieldCheck size={14} className="mr-1.5" />}
                        {verifyingEmails ? 'Verifying emails...' : `Verify emails (${detail.contacts.length})`}
                      </Button>
                    )}
                  </div>
                  {detail?.contacts && detail.contacts.length > 0 ? (
                    <div className="space-y-1.5">
                      {detail.contacts.map((c, i) => {
                        const statusColor = {
                          mx_confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                          provider_verified: 'bg-green-50 text-green-800 border-green-300',
                          syntactically_valid: 'bg-blue-50 text-blue-700 border-blue-200',
                          risky: 'bg-amber-50 text-amber-700 border-amber-200',
                          invalid: 'bg-red-50 text-red-700 border-red-200',
                        }[c.email_status] || 'bg-gray-100 text-gray-600 border-gray-200';
                        const typeColor = c.email_type === 'named_business' ? 'text-emerald-700' :
                                         c.email_type === 'generic_business' ? 'text-blue-700' :
                                         c.email_type === 'personal_risky' ? 'text-red-700' : 'text-gray-500';
                        return (
                          <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded bg-white border border-gray-100 text-sm">
                            {c.is_primary && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" title="Primary contact" />}
                            <span className="font-mono text-gray-800 truncate flex-1">{c.email}</span>
                            <span className={`text-[10px] font-mono ${typeColor}`}>{c.email_type?.replace('_', ' ')}</span>
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono font-medium border ${statusColor}`}>{c.email_status || 'unverified'}</span>
                            {c.full_name && <span className="text-xs text-gray-400 truncate max-w-[100px]">{c.full_name}</span>}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 py-2">No contacts. Add contacts or generate emails from the company detail page.</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button onClick={() => updateStatus(current.id, 'approved')} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 h-12 text-base" data-testid="review-approve-btn">
                    <Check size={20} className="mr-2" /> Approve
                  </Button>
                  <Button onClick={() => updateStatus(current.id, 'rejected')} variant="outline" className="flex-1 border-red-200 text-red-700 hover:bg-red-50 h-12 text-base" data-testid="review-reject-btn">
                    <XIcon size={20} className="mr-2" /> Reject
                  </Button>
                </div>

                {/* Quick link */}
                <a href={`/app/companies/${current.id}`} className="block text-center text-xs text-gray-400 hover:text-gray-700 mt-3 underline">
                  Open full company detail
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
